<template>
  <header class="border-b shadow-sm transition-colors duration-300" :class="isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'">
    <div class="flex items-center justify-between h-8 px-3">
      <!-- Left Section -->
      <div class="flex items-center space-x-2">
        <!-- Mobile Menu Button -->
        <button
          @click="toggleSidebar"
          :class="isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'"
          class="p-1.5 rounded-md transition-colors lg:hidden"
        >
          <i class="fas fa-bars text-sm"></i>
        </button>
        
        <!-- Logo -->
        <div class="flex items-center space-x-1.5">
          <div class="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center">
            <i class="fas fa-ship text-white text-xs"></i>
          </div>
          <div>
            <h1 :class="isDarkMode ? 'text-white' : 'text-gray-900'" class="font-bold text-sm">multicon</h1>
            <p :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'" class="text-[10px] leading-tight">Real-time vessel monitoring</p>
          </div>
        </div>
        
        <!-- DESKTOP NAVIGATION -->
        <nav class="hidden lg:flex items-center space-x-0.5 ml-4">
          <router-link
            to="/"
            @click="setActivePage('map')"
            class="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
            :class="getNavClass('/')"
          >
            <i class="fas fa-map-marked-alt mr-1.5 text-xs"></i>
            Map
          </router-link>
          
          <router-link
            to="/cameras"
            @click="setActivePage('cameras')"
            class="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
            :class="getNavClass('/cameras')"
          >
            <i class="fas fa-video mr-1.5 text-xs"></i>
            Camera
          </router-link>

          <!-- Stats Button - ONLY FOR ADMIN USERS -->
          <router-link
            v-if="isAdmin"
            to="/stats"
            @click="setActivePage('stats')"
            class="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
            :class="getNavClass('/stats')"
          >
            <i class="fas fa-chart-bar mr-1.5 text-xs"></i>
            Stats
          </router-link>

          <!-- Admin Config Button (Only for admin users) -->
          <router-link
            v-if="isAdmin"
            to="/admin/config"
            @click="setActivePage('config')"
            class="px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
            :class="getNavClass('/admin/config')"
          >
            <i class="fas fa-cog mr-1.5 text-xs"></i>
            Config
          </router-link>
        </nav>
      </div>
      
      <!-- Right Section -->
      <div class="flex items-center space-x-2">       
        <!-- Source Indicator -->
        <div :class="isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'" class="hidden sm:block px-2 py-1 rounded-md text-xs">
          <span class="text-gray-600"></span>
          <span class="font-medium ml-0.5">{{ userSourceLabel }}</span>
        </div>
        
        <!-- Connection Status -->
        <div :class="isDarkMode ? 'bg-gray-800' : 'bg-gray-100'" class="flex items-center space-x-1 px-2 py-1 rounded-md">
          <span 
            class="w-1.5 h-1.5 rounded-full"
            :class="connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'"
          ></span>
          <span :class="isDarkMode ? 'text-gray-400' : 'text-gray-600'" class="text-xs hidden sm:inline">
            {{ connected ? 'Live' : 'Disconnected' }}
          </span>
        </div>
        
        <!-- Theme Toggle Button -->
        <button
          @click="toggleTheme"
          :class="isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-yellow-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'"
          class="p-1.5 rounded-md transition-colors"
          :title="isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
        >
          <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'" class="text-sm"></i>
        </button>
        
        <!-- User Menu (Mobile Navigation & Settings) -->
        <div class="relative">
          <button
            @click="showUserMenu = !showUserMenu"
            :class="isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'"
            class="flex items-center space-x-1.5 px-2 py-1.5 rounded-md transition-colors"
          >
            <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white">
              <i class="fas fa-user text-xs"></i>
            </div>
            <span :class="isDarkMode ? 'text-gray-300' : 'text-gray-700'" class="hidden md:block text-xs font-medium">{{ user?.username }}</span>
            <i :class="isDarkMode ? 'text-gray-500' : 'text-gray-500'" class="fas fa-chevron-down text-[10px]"></i>
          </button>
          
          <!-- Dropdown Menu -->
          <div
            v-if="showUserMenu"
            :class="isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'"
            class="absolute right-0 mt-1 w-52 rounded-md shadow-lg py-1 z-50 border"
          >
            <!-- User Info -->
            <div :class="isDarkMode ? 'border-gray-700' : ''" class="px-3 py-2 border-b">
              <p :class="isDarkMode ? 'text-white' : 'text-gray-900'" class="text-xs font-medium">{{ user?.username }}</p>
              <p :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'" class="text-[10px]">{{ user?.role || 'User' }}</p>
            </div>
            
            <!-- Navigation Links (Mobile Only - muncul saat layar < 1024px) -->
            <div class="lg:hidden border-b" :class="isDarkMode ? 'border-gray-700' : 'border-gray-100'">
              <div class="px-2 py-1">
                <p class="text-[10px] font-semibold px-2 py-1" :class="isDarkMode ? 'text-gray-400' : 'text-gray-500'">MENU</p>
              </div>
              
              <router-link
                to="/"
                @click="closeMenuAndSetPage('map')"
                class="flex items-center px-3 py-2 text-xs transition-colors"
                :class="[
                  route.path === '/' 
                    ? (isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600')
                    : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                ]"
              >
                <i class="fas fa-map-marked-alt w-5 text-xs"></i>
                <span>Map View</span>
              </router-link>
              
              <router-link
                to="/cameras"
                @click="closeMenuAndSetPage('cameras')"
                class="flex items-center px-3 py-2 text-xs transition-colors"
                :class="[
                  route.path === '/cameras'
                    ? (isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600')
                    : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                ]"
              >
                <i class="fas fa-video w-5 text-xs"></i>
                <span>Cameras</span>
              </router-link>

              <!-- Stats Button in Mobile Menu - ONLY FOR ADMIN -->
              <router-link
                v-if="isAdmin"
                to="/stats"
                @click="closeMenuAndSetPage('stats')"
                class="flex items-center px-3 py-2 text-xs transition-colors"
                :class="[
                  route.path === '/stats'
                    ? (isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600')
                    : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                ]"
              >
                <i class="fas fa-chart-bar w-5 text-xs"></i>
                <span>Statistics</span>
              </router-link>

              <!-- Admin Config Menu (Mobile Only) -->
              <router-link
                v-if="isAdmin"
                to="/admin/config"
                @click="closeMenuAndSetPage('config')"
                class="flex items-center px-3 py-2 text-xs transition-colors"
                :class="[
                  route.path === '/admin/config'
                    ? (isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600')
                    : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                ]"
              >
                <i class="fas fa-cog w-5 text-xs"></i>
                <span>Configuration</span>
              </router-link>
            </div>
            
            <!-- Dark Mode Toggle in Menu -->
            <button
              @click="toggleTheme"
              :class="isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'"
              class="w-full text-left px-3 py-2 text-xs flex items-center"
            >
              <i :class="isDarkMode ? 'fas fa-sun w-5' : 'fas fa-moon w-5'" class="text-xs"></i>
              <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
            </button>
            
            <!-- Logout -->
            <hr :class="isDarkMode ? 'border-gray-700' : 'border-gray-100'" class="my-1">
            
            <button
              @click="handleLogout"
              :class="isDarkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-50 text-red-600'"
              class="w-full text-left px-3 py-2 text-xs flex items-center"
            >
              <i class="fas fa-sign-out-alt w-5 text-xs"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { useUIStore } from '../../stores/ui'
import { useThemeStore } from '../../stores/theme'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const uiStore = useUIStore()
const themeStore = useThemeStore()

const props = defineProps({
  connected: {
    type: Boolean,
    default: false
  },
  shipCount: {
    type: Number,
    default: 0
  },
  filteredShipCount: {
    type: Number,
    default: 0
  }
})

const showUserMenu = ref(false)
const isDarkMode = computed(() => themeStore.isDarkMode)

const user = computed(() => authStore.user)
const userSourceLabel = computed(() => {
  if (authStore.userSource === 'all') return 'All Sources'
  return authStore.userSource
})

// Check if user is admin
const isAdmin = computed(() => {
  return authStore.user?.role === 'admin'
})

// Fungsi untuk mendapatkan class navigasi berdasarkan path
function getNavClass(path) {
  const isActive = route.path === path
  if (isDarkMode.value) {
    return isActive 
      ? 'bg-blue-900/50 text-blue-400' 
      : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
  }
  return isActive 
    ? 'bg-blue-50 text-blue-600' 
    : 'text-gray-600 hover:bg-gray-100'
}

function setActivePage(page) {
  uiStore.setActivePage(page)
}

function closeMenuAndSetPage(page) {
  showUserMenu.value = false
  uiStore.setActivePage(page)
}

function toggleTheme() {
  themeStore.toggleTheme()
}

function toggleSidebar() {
  uiStore.toggleSidebar()
}

function handleLogout() {
  showUserMenu.value = false
  authStore.logout()
  router.push('/login')
}

// Fungsi untuk menutup menu saat klik di luar
function handleClickOutside(event) {
  const userMenuButton = document.querySelector('.relative:last-child button')
  const dropdownMenu = document.querySelector('.relative:last-child > div')
  
  if (dropdownMenu && !dropdownMenu.contains(event.target) && 
      userMenuButton && !userMenuButton.contains(event.target) && 
      showUserMenu.value) {
    showUserMenu.value = false
  }
}

// Event listener untuk klik di luar
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Additional styles for better visibility */
.router-link-active {
  font-weight: 600;
}

/* Smooth transitions */
.router-link-active,
.router-link-exact-active {
  transition: all 0.2s ease;
}
</style>